import HotmathHead from './hotmath-head';
import HotmathTitle from './hotmath-title';
import type { HotmathPage } from '../@types/hotmath-page';
import useHotmathHelpPage from './use-hotmath-help-page';
import useHotmathStructuredData from './use-hotmath-structured-data';
import { CmsResult } from '@nrdy-marketing-engine/nsp/src/fetch-nsp-data';
import { Newrelic } from '@nrdy-marketing-engine/newrelic';
import { NspProvider } from '@nrdy-marketing-engine/nsp/src/nsp-context';
import { StructuredData } from '@nrdy-marketing-engine/structured-data';
import './hotmath.css';
import NspContainers from '@nrdy-marketing-engine/nsp-component/src/nsp-containers';
import type { LinkBlockList } from '@nrdy-marketing-engine/internal-links/@types/schema';
import LinkBlocksContainer from '@nrdy-marketing-engine/internal-links/src/link-blocks-container';
import SeoLayout from '@nrdy-marketing-engine/layouts/src/seo-layout';
import AppsBar from '@nrdy-marketing-engine/apps-bar/src/apps-bar';

interface HotmathHelpPageProps {
  nspData: CmsResult & { hotmathPage: HotmathPage };
  slug: string;
  browserTimingHeader?: string;
  linkBlocks?: LinkBlockList;
}

const getAlternates = (slug: string) => [
  {
    href: `/hotmath/${slug.replace(/hotmath_help\/(spanish\/)?/, 'hotmath_help/')}`,
    hrefLang: 'x-default',
  },
  {
    href: `/hotmath/${slug.replace(/hotmath_help\/(spanish\/)?/, 'hotmath_help/spanish/')}`,
    hrefLang: 'es',
  },
];

/**
 * Renders a Hotmath Help page (/hotmath/hotmath_help/*) with the data retrieved from NSP.
 */
export default function HotmathHelpPage({
  nspData,
  slug,
  browserTimingHeader,
  linkBlocks,
}: HotmathHelpPageProps) {
  const { hotmathPage } = nspData;
  const { body, seoMetadata, indexable } = useHotmathHelpPage(hotmathPage);
  const structuredData = useHotmathStructuredData(slug, seoMetadata.title);

  return (
    <NspProvider value={nspData}>
      <HotmathHead />
      <Newrelic browserTimingHeader={browserTimingHeader} />
      <SeoLayout
        metadata={{
          title: seoMetadata.title,
          description:
            seoMetadata.description ||
            'Varsity Tutors connects you to top tutors through its award-winning live learning platform for private in-home or online tutoring in your area.',
          indexable,
          canonical: `/hotmath/${slug}`,
          lang: slug.includes('spanish/') ? 'es' : 'en',
          alternates: getAlternates(slug),
        }}
        path={`/hotmath/${slug}`}
      >
        <StructuredData data={structuredData} />
        <HotmathTitle />
        <NspContainers items={body} />
        <LinkBlocksContainer linkBlocks={linkBlocks} />
        <AppsBar />
      </SeoLayout>
    </NspProvider>
  );
}
